import { MAX_FOCUS_PROJECTS, doneProgress, nextStep, projectOverview, weeklyReview } from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { api, errorMessage } from "../api";
import { NextStepLink } from "../components/Focus";
import { EmptyState, useToast } from "../components/feedback";
import { dateTime } from "../format";
import { Icon } from "../icons";

/**
 * Wochenrückblick (UX-10): zehn Minuten am Handy. Was ist fertig, was ist im Fokus, was hängt,
 * und welches Projekt kommt als Nächstes. Inhalt aus packages/domain/src/focus.ts.
 */
export function Review() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  const last = useQuery({ queryKey: ["weekly-review"], queryFn: api.weeklyReview });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["projects"] });
  };
  const unfocus = useMutation({
    mutationFn: (id: string) => api.setProjectFocus(id, false),
    onSuccess: async (p) => {
      await refresh();
      toast(`„${p.title}“ ist geparkt`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const focus = useMutation({
    mutationFn: (id: string) => api.setProjectFocus(id, true),
    onSuccess: async (p) => {
      await refresh();
      toast(`„${p.title}“ ist im Fokus`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const done = useMutation({
    mutationFn: api.completeWeeklyReview,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["weekly-review"] });
      toast("Rückblick erledigt. Der nächste ist in sieben Tagen fällig.");
      void navigate({ to: "/" });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  const list = projects.data ?? [];
  const orders = workOrders.data ?? [];
  const rows = list.map((p) => {
    const own = orders.filter((o) => o.projectId === p.id);
    return { project: p, own, overview: projectOverview(p.createdAt, own) };
  });
  const review = weeklyReview(
    rows.map((r) => ({ ...r.project, lastActivityAt: r.overview.lastActivityAt })),
    orders,
    new Date(),
  );
  const stalled = new Set(review.stalled.map((p) => p.id));
  const free = MAX_FOCUS_PROJECTS - review.focus.length;
  const parked = rows.filter((r) => !r.project.focus && r.project.lifecycle === "development");
  const busy = unfocus.isPending || focus.isPending;

  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <h1>
            Wochen<span className="glow-word">rückblick</span>
          </h1>
          <p className="hero-sub">
            Zehn Minuten: Was ist fertig, was hängt, was kommt als Nächstes.
            {last.data?.lastReviewAt && ` Letzter Rückblick ${dateTime(last.data.lastReviewAt)}.`}
          </p>
        </div>
      </section>

      <section className="panel panel--section" aria-labelledby="doneHead">
        <h2 id="doneHead">Diese Woche fertig</h2>
        {review.released.length === 0 && review.metCriteria.length === 0 ? (
          <p className="muted">Diese Woche ist nichts fertig geworden. Das ist ein Hinweis, kein Vorwurf.</p>
        ) : (
          <ul className="review-list">
            {review.released.map((o) => (
              <li key={o.id}>
                <Icon name="rocket" /> Ausgeliefert:{" "}
                <Link to="/work-orders/$orderId" params={{ orderId: o.id }} className="link">
                  {o.title}
                </Link>
              </li>
            ))}
            {review.metCriteria.map((c) => (
              <li key={`${c.projectId}-${c.text}`}>
                <Icon name="check" /> {c.projectTitle}: {c.text}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel panel--section" aria-labelledby="focusHead">
        <h2 id="focusHead">
          Im Fokus ({review.focus.length} von {MAX_FOCUS_PROJECTS})
        </h2>
        {review.focus.length === 0 && (
          <EmptyState icon="folder" title="Kein Projekt im Fokus" text="Wähle unten bis zu drei Projekte." />
        )}
        <ul className="review-list">
          {rows
            .filter((r) => r.project.focus && r.project.lifecycle !== "closed")
            .map(({ project: p, own }) => {
              const { met, total } = doneProgress(p.doneCriteria);
              return (
                <li key={p.id} className="project-card portfolio-card">
                  <span className="order-main">
                    <Link to="/projects/$projectId" params={{ projectId: p.id }} className="portfolio-title">
                      <strong>{p.title}</strong>
                    </Link>
                    <span className="portfolio-facts">
                      {total > 0 ? `${met} von ${total} Kriterien erfüllt` : "„Fertig heißt“ fehlt noch"}
                      {stalled.has(p.id) && " · hängt seit über zwei Wochen: parken oder kleiner schneiden?"}
                    </span>
                    <NextStepLink step={nextStep(p, own)} projectId={p.id} />
                  </span>
                  <span className="portfolio-badges">
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      disabled={busy}
                      onClick={() => unfocus.mutate(p.id)}
                    >
                      Parken
                    </button>
                  </span>
                </li>
              );
            })}
        </ul>
      </section>

      {free > 0 && parked.length > 0 && (
        <section className="panel panel--section" aria-labelledby="nextHead">
          <h2 id="nextHead">Was kommt als Nächstes?</h2>
          <p className="muted">
            {free === 1 ? "Ein Fokusplatz ist frei." : `${free} Fokusplätze sind frei.`} Nimm nur auf, was du
            wirklich fertigstellen willst.
          </p>
          <ul className="review-list">
            {parked.map(({ project: p }) => (
              <li key={p.id} className="project-card portfolio-card">
                <span className="order-main">
                  <Link to="/projects/$projectId" params={{ projectId: p.id }} className="portfolio-title">
                    <strong>{p.title}</strong>
                  </Link>
                  <span className="muted">{p.goal}</span>
                </span>
                <span className="portfolio-badges">
                  <button
                    type="button"
                    className="btn btn--small"
                    disabled={busy}
                    onClick={() => focus.mutate(p.id)}
                  >
                    <Icon name="target" /> In den Fokus
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="idea-actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={done.isPending}
          onClick={() => done.mutate()}
        >
          <span className="btn-label">
            <Icon name="check" /> Rückblick erledigt
          </span>
        </button>
      </div>
    </>
  );
}
